import { useState, useRef, useEffect, useCallback, KeyboardEvent, CSSProperties } from "react";

// ── Types ────────────────────────────────────────────────────────────────────

export interface SelectOption {
  /** Unique identifier */
  value: string;
  /** Left-aligned label (primary) */
  label: string;
  /** Right-aligned label (secondary, e.g. price, count, date) */
  meta?: string;
  /** Optional small descriptor shown below the option row */
  description?: string;
  disabled?: boolean;
  sort?: number;
  style?: CSSProperties;
  styleDesc?: CSSProperties;
  styleMeta?: CSSProperties;
}

export interface DualSelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string, option: SelectOption) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  /** Width of the dropdown trigger, defaults to "100%" */
  width?: string | number;
  /** Override any part of the default theme */
  theme?: Partial<DualSelectTheme>;
}

// ── Theme ─────────────────────────────────────────────────────────────────────
//
//  All visual decisions live here. Pass `theme` prop to override any token.
//
//  Color groups:
//    bg.*        — surface backgrounds
//    text.*      — foreground / text colors
//    border.*    — border colors
//
//  Size groups:
//    radius.*    — border radii
//    height.*    — component heights
//    font.*      — font families & sizes
//    spacing.*   — padding / gap values
//
//  Motion:
//    transition  — shared timing for color/opacity transitions
//    chevronTransition — chevron rotation timing

export interface DualSelectTheme {
  // ── Colors ──────────────────────────────────────────────────────────────────
  bg: {
    trigger: string;         // trigger button background
    dropdown: string;        // dropdown panel background
    optionHover: string;     // option row on hover
    optionSelected: string;  // option row when selected
  };
  text: {
    label: string;           // field label above the trigger
    placeholder: string;     // trigger text when nothing is selected
    triggerValue: string;    // trigger text when a value is selected
    triggerMeta: string;     // right-side meta text in the trigger
    optionLabel: string;     // option primary label (unselected)
    optionLabelSelected: string; // option primary label (selected)
    optionMeta: string;      // option right-side meta text
    optionDesc: string;      // option description subtitle
    chevron: string;         // chevron icon color
    selectedDot: string;     // selected-indicator dot color
  };
  border: {
    trigger: string;         // trigger border (closed)
    triggerOpen: string;     // trigger border (open / focused)
    triggerFocusRing: string;// box-shadow focus ring color
    dropdown: string;        // dropdown panel border
    divider: string;         // between-option divider line
  };

  // ── Radii ───────────────────────────────────────────────────────────────────
  radius: {
    trigger: string;
    dropdown: string;
    option: string;
  };

  // ── Sizing ──────────────────────────────────────────────────────────────────
  height: {
    dropdown: string;
    trigger: string;         // trigger button height
    divider: string;         // option divider height
  };
  font: {
    family: string;
    sizeLabel: string;
    sizeTrigger: string;
    sizeMeta: string;
    sizeDesc: string;
  };
  spacing: {
    triggerPaddingX: string;
    triggerPaddingY: string;
    optionPaddingX: string;
    optionPaddingY: string;
    dropdownPadding: string;
    dropdownGap: string;     // gap between trigger bottom and dropdown top
    optionGap: string;       // gap between label and meta in an option row
    maxDropdownHeight: string;
  };

  // ── Motion ──────────────────────────────────────────────────────────────────
  transition: string;
  chevronTransition: string;

  // ── Misc ────────────────────────────────────────────────────────────────────
  dropdownShadow: string;
  disabledOpacity: number;
  selectedDotSize: string;
}

export const defaultTheme: DualSelectTheme = {
  bg: {
    trigger:         "#0e0e0e",
    dropdown:        "#111111",
    optionHover:     "#181818",
    optionSelected:  "#1c1c1c",
  },
  text: {
    label:                "#888888",
    placeholder:          "#555555",
    triggerValue:         "#e8e8e8",
    triggerMeta:          "#aaaaaa",
    optionLabel:          "#cccccc",
    optionLabelSelected:  "#f0f0f0",
    optionMeta:           "#666666",
    optionDesc:           "#444444",
    chevron:              "#555555",
    selectedDot:          "#e8e8e8",
  },
  border: {
    trigger:         "#2a2a2a",
    triggerOpen:     "#555555",
    triggerFocusRing:"rgba(255,255,255,0.06)",
    dropdown:        "#2a2a2a",
    divider:         "#1e1e1e",
  },
  radius: {
    trigger:  "6px",
    dropdown: "8px",
    option:   "5px",
  },
  height: {
    dropdown: "auto",
    trigger: "44px",
    divider: "1px",
  },
  font: {
    family:       "'DM Mono', 'Fira Mono', 'Courier New', monospace",
    sizeLabel:    "11px",
    sizeTrigger:  "13px",
    sizeMeta:     "12px",
    sizeDesc:     "11px",
  },
  spacing: {
    triggerPaddingX:  "14px",
    triggerPaddingY:  "0px",
    optionPaddingX:   "10px",
    optionPaddingY:   "9px",
    dropdownPadding:  "4px",
    dropdownGap:      "5px",
    optionGap:        "12px",
    maxDropdownHeight:"280px",
  },
  transition:        "border-color 0.15s, box-shadow 0.15s",
  chevronTransition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  dropdownShadow:    "0 8px 32px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)",
  disabledOpacity:   0.4,
  selectedDotSize:   "5px",
};

// ── Preset themes (ready to use) ─────────────────────────────────────────────

export const lightTheme: Partial<DualSelectTheme> = {
  bg: {
    trigger:        "#ffffff",
    dropdown:       "#ffffff",
    optionHover:    "#f5f5f5",
    optionSelected: "#efefef",
  },
  text: {
    label:               "#999999",
    placeholder:         "#bbbbbb",
    triggerValue:        "#111111",
    triggerMeta:         "#888888",
    optionLabel:         "#333333",
    optionLabelSelected: "#000000",
    optionMeta:          "#999999",
    optionDesc:          "#bbbbbb",
    chevron:             "#aaaaaa",
    selectedDot:         "#111111",
  },
  border: {
    trigger:         "#e0e0e0",
    triggerOpen:     "#aaaaaa",
    triggerFocusRing:"rgba(0,0,0,0.08)",
    dropdown:        "#e0e0e0",
    divider:         "#f0f0f0",
  },
  dropdownShadow: "0 8px 24px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)",
};

// ── Style factory (derives inline styles from theme) ──────────────────────────

function makeStyles(t: DualSelectTheme) {
  return {
    root: {
      fontFamily: t.font.family,
      position: "relative" as const,
    },
    label: {
      display: "block",
      fontSize: t.font.sizeLabel,
      fontWeight: 500,
      letterSpacing: "0.08em",
      textTransform: "uppercase" as const,
      color: t.text.label,
      marginBottom: "8px",
    } satisfies React.CSSProperties,
    trigger: (open: boolean, disabled: boolean): React.CSSProperties => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      padding: `${t.spacing.triggerPaddingY} ${t.spacing.triggerPaddingX}`,
      height: t.height.trigger,
      background: t.bg.trigger,
      border: `1px solid ${open ? t.border.triggerOpen : t.border.trigger}`,
      borderRadius: t.radius.trigger,
      cursor: disabled ? "not-allowed" : "pointer",
      outline: "none",
      transition: t.transition,
      boxShadow: open ? (t.border.triggerFocusRing.startsWith('!') ? t.border.triggerFocusRing.slice(1) :  `0 0 0 3px ${t.border.triggerFocusRing}`) : "none",
      opacity: disabled ? t.disabledOpacity : 1,
    }),
    triggerLeft: (hasValue: boolean): React.CSSProperties => ({
      fontSize: t.font.sizeTrigger,
      fontWeight: hasValue ? 500 : 400,
      color: hasValue ? t.text.triggerValue : t.text.placeholder,
      letterSpacing: "0.01em",
      whiteSpace: "nowrap" as const,
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "55%",
    }),
    triggerRight: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      flexShrink: 0,
    } satisfies React.CSSProperties,
    triggerMeta: (hasValue: boolean): React.CSSProperties => ({
      fontSize: t.font.sizeMeta,
      color: hasValue ? t.text.triggerMeta : "transparent",
      fontVariantNumeric: "tabular-nums" as const,
      letterSpacing: "0.02em",
    }),
    chevron: (open: boolean): React.CSSProperties => ({
      width: "14px",
      height: "14px",
      color: t.text.chevron,
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      transition: t.chevronTransition,
      flexShrink: 0,
    }),
    dropdown: {
      position: "absolute" as const,
      top: `calc(100% + ${t.spacing.dropdownGap})`,
      left: 0,
      right: 0,
      background: t.bg.dropdown,
      border: `1px solid ${t.border.dropdown}`,
      borderRadius: t.radius.dropdown,
      height: t.height.dropdown,
      zIndex: 50,
      overflow: "hidden",
      boxShadow: t.dropdownShadow,
    } satisfies React.CSSProperties,
    optionList: {
      listStyle: "none",
      margin: 0,
      padding: t.spacing.dropdownPadding,
      maxHeight: t.spacing.maxDropdownHeight,
      overflowY: "auto" as const,
      height: "-webkit-fill-available",
    },
    option: (hovered: boolean, selected: boolean, disabled: boolean): React.CSSProperties => ({
      display: "flex",
      flexDirection: "column",
      padding: `${t.spacing.optionPaddingY} ${t.spacing.optionPaddingX}`,
      borderRadius: t.radius.option,
      cursor: disabled ? "not-allowed" : "pointer",
      background: selected ? t.bg.optionSelected : hovered ? t.bg.optionHover : "transparent",
      transition: "background 0.1s",
      opacity: disabled ? t.disabledOpacity : 1,
      outline: "none",
    }),
    optionRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: t.spacing.optionGap,
    } satisfies React.CSSProperties,
    optionLabel: (selected: boolean): React.CSSProperties => ({
      fontSize: t.font.sizeTrigger,
      fontWeight: selected ? 500 : 400,
      color: selected ? t.text.optionLabelSelected : t.text.optionLabel,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      letterSpacing: "0.01em",
    }),
    optionMeta: {
      fontSize: t.font.sizeMeta,
      color: t.text.optionMeta,
      fontVariantNumeric: "tabular-nums" as const,
      letterSpacing: "0.03em",
      flexShrink: 0,
    } satisfies React.CSSProperties,
    optionDesc: {
      marginTop: "2px",
      fontSize: t.font.sizeDesc,
      color: t.text.optionDesc,
      letterSpacing: "0.02em",
    } satisfies React.CSSProperties,
    divider: {
      height: t.height.divider,
      background: t.border.divider,
    } satisfies React.CSSProperties,
    selectedDot: {
      width: t.selectedDotSize,
      height: t.selectedDotSize,
      borderRadius: "50%",
      background: t.text.selectedDot,
      flexShrink: 0,
      marginLeft: "4px",
    } satisfies React.CSSProperties,
  };
}

// ── Chevron SVG ───────────────────────────────────────────────────────────────

function ChevronIcon({ style }: { style: React.CSSProperties }) {
  return (
    <svg style={style} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2,5 7,9 12,5" />
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DualSelect({
  options,
  value,
  onChange,
  placeholder = "Select an option…",
  label,
  disabled = false,
  width = "100%",
  theme,
}: DualSelectProps) {
  const resolvedTheme: DualSelectTheme = theme
    ? {
        ...defaultTheme,
        ...theme,
        bg:      { ...defaultTheme.bg,      ...theme.bg },
        text:    { ...defaultTheme.text,    ...theme.text },
        border:  { ...defaultTheme.border,  ...theme.border },
        radius:  { ...defaultTheme.radius,  ...theme.radius },
        height:  { ...defaultTheme.height,  ...theme.height },
        font:    { ...defaultTheme.font,    ...theme.font },
        spacing: { ...defaultTheme.spacing, ...theme.spacing },
      }
    : defaultTheme;

  const s = makeStyles(resolvedTheme);

  const [open, setOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selected = options.find((o) => o.value === value) ?? null;
  const focusedIndex = hoveredIndex >= 0 ? hoveredIndex : options.findIndex((o) => o.value === value);

  const close = useCallback(() => {
    setOpen(false);
    setHoveredIndex(-1);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [close]);

  const select = (opt: SelectOption) => {
    if (opt.disabled) return;
    onChange?.(opt.value, opt);
    close();
    triggerRef.current?.focus();
  };

  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHoveredIndex(focusedIndex >= 0 ? focusedIndex : 0);
    } else if (e.key === "Escape") {
      close();
    }
  };

  const handleListKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHoveredIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHoveredIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (hoveredIndex >= 0) select(options[hoveredIndex]);
    } else if (e.key === "Escape" || e.key === "Tab") {
      close();
      triggerRef.current?.focus();
    }
  };

  return (
    <div ref={rootRef} style={{ ...s.root, width }}>
      {label && <label style={s.label}>{label}</label>}

      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label ?? "Select"}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={handleTriggerKeyDown}
        style={s.trigger(open, disabled)}
      >
        <span style={s.triggerLeft(!!selected)}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={s.triggerRight}>
          <span style={s.triggerMeta(!!selected)}>{selected?.meta ?? ""}</span>
          <ChevronIcon style={s.chevron(open)} />
        </span>
      </button>

      {open && (
        <div style={s.dropdown}>
          <ul
            role="listbox"
            tabIndex={-1}
            aria-label={label ?? "Options"}
            style={s.optionList}
            onKeyDown={handleListKeyDown}
            autoFocus
          >
            {options.map((opt, i) => {
              const isSelected = opt.value === value;
              const isHovered  = hoveredIndex === i;
              return (
                <li key={opt.value}>
                  <div
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled}
                    tabIndex={-1}
                    style={{
                      ...s.option(isHovered, isSelected, !!opt.disabled),
                      ...opt.style
                    }}
                    onMouseEnter={() => !opt.disabled && setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(-1)}
                    onClick={() => select(opt)}
                  >
                    <div style={s.optionRow}>
                      <span style={s.optionLabel(isSelected)}>{opt.label}</span>
                      <span style={s.optionRow}>
                        <span style={{
                          ...s.optionMeta,
                          ...opt.styleMeta
                        }}>{opt.meta}</span>
                        {isSelected && <span style={s.selectedDot} />}
                      </span>
                    </div>
                    {opt.description && (
                      <span style={{
                        ...s.optionDesc,
                        ...opt.styleDesc
                      }}>{opt.description}</span>
                    )}
                  </div>
                  {i < options.length - 1 && <div style={s.divider} />}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}